import type { Presence } from '~/data/types'
import type { AgentStyle } from '~/game/agentCharacters'

/**
 * The choreography contract: live Zunou activity, normalized into the handful
 * of game-meaningful events the office scene knows how to animate.
 */
export type GameEvent =
  | { kind: 'presence'; userId: string; status: Presence }
  | {
      kind: 'message'
      pulseId: string | null
      userId: string | null
      text: string | null
      topicId?: string | null
      /** Showcase-simulated chatter — animates a transient visit, never a
       *  permanent relocation (only REAL messages move someone's seat). */
      ambient?: boolean
    }
  | {
      kind: 'task'
      action: 'created' | 'updated' | 'deleted'
      pulseId: string
      taskId: string | null
      assigneeUserIds: string[]
      /** Who performed the action (created/updated/completed it). */
      actorId?: string | null
      /** Showcase-simulated — animate in-scene, but keep it OFF the surfaces
       *  that present themselves as real activity (chips/ticker/refetches). */
      ambient?: boolean
    }
  | {
      kind: 'note'
      action: 'created' | 'updated' | 'deleted'
      pulseId: string
      noteId: string | null
      actorId: string | null
      title: string | null
    }
  | { kind: 'typing'; pulseId: string; userId: string }
  /** Fresh per-room task counts — redraws the wall task board. */
  | {
      kind: 'taskBoard'
      pulseId: string
      open: number
      doing: number
      done: number
      overdue: number
    }
  /** Fresh per-room notes info — redraws the shelf; fresh = new/edited note. */
  | {
      kind: 'notesShelf'
      pulseId: string
      count: number
      fresh: boolean
    }
  /** Pan the camera to a person and soft-follow them (panel "locate"). */
  | { kind: 'cameraFollow'; userId: string }
  /** Glide the camera to a room (panel "locate"). */
  | { kind: 'cameraRoom'; pulseId: string }
  | { kind: 'reaction'; userId: string; symbol: string; ambient?: boolean }
  | { kind: 'insight'; pulseId: string }
  | { kind: 'camera'; action: 'in' | 'out' | 'reset' }
  /** A meeting session changed (started/ended/recurring-created) on a room's
   *  pulse — re-resolve meetings from a refetched snapshot (no page reload). */
  | { kind: 'meetingRefetch'; pulseId: string }
  /** An agent's box reported fresh work — float it as a thought bubble. */
  | { kind: 'agentWork'; userId: string; summary: string }
  /** An agent's learning mode (🎓 LEARNING / 🔒 LOCKED badge; PROPOSE quiet).
   *  inboxCount = its whats_new depth, for desk-paper cues. */
  | { kind: 'agentMode'; userId: string; mode: string | null; inboxCount: number }
  /** The agent has lessons/skills awaiting human review — amber "!" marker. */
  | { kind: 'agentNeedsYou'; userId: string; on: boolean }
  /** The org's HR-manager agents (canManageAgents) — the HR emblem (and the
   *  gold top hat on the unstyled robot). Full set each emit; human-set and
   *  can flip at runtime. */
  | { kind: 'agentHr'; userIds: string[] }
  /** The org's PERSONAL agents (roleKind PERSONAL: run on their owner's own
   *  computer) — the PA emblem, teal by default. Full set each emit, like agentHr. */
  | { kind: 'agentPersonal'; userIds: string[] }
  /** The org's EA agents (roleKind EA) — the EA emblem, coral by default. Full set each emit. */
  | { kind: 'agentEa'; userIds: string[] }
  /** The org's Stewards (they look after their principal's Zunou account for
   *  them) — the ★ emblem; staffUserIds, the Steward staff tier, a red ★.
   *  Full sets each emit; people switch them, so they flip at runtime. */
  | { kind: 'agentSteward'; userIds: string[]; staffUserIds: string[] }
  /** The agents' stored looks (AGENT_ONBOARDING_SPEC.md 3a) by user id: the
   *  character, colour, hat, cape and badge. Full map each emit; an agent not
   *  in it is the classic robot in its role's colour. */
  | { kind: 'agentStyle'; styles: Record<string, AgentStyle> }
  /** The agents whose readiness is UNPLUGGED (no seat, plan or engine): grey
   *  and slumped with a loose plug. Full set each emit. */
  | { kind: 'agentUnplugged'; userIds: string[] }
  /** An office box's live health (onboarding checklist) — drives the in-world
   *  server rack's LEDs: ACTION = red pulse, WARN = amber, OK = green. */
  | {
      kind: 'officeHealth'
      officeId: string
      name: string
      level: 'OK' | 'WARN' | 'ACTION'
      issues: string[]
    }

type Handler = (event: GameEvent) => void

export class EventBus {
  private handlers = new Set<Handler>()

  on(handler: Handler): () => void {
    this.handlers.add(handler)
    return () => this.handlers.delete(handler)
  }

  emit(event: GameEvent): void {
    for (const handler of this.handlers) {
      try {
        handler(event)
      } catch {
        // One bad handler must not break the rest of the choreography.
      }
    }
  }
}
